<script setup lang="ts">
import maplibregl from 'maplibre-gl'
import { inject, onUnmounted, shallowRef, watch, type ShallowRef } from 'vue'

import type { TrackEditReferenceDiffSegment } from '@/utils/trackEditReferenceDiff'
import { buildTrackHoverPopupHtml, estimateHoveredDistanceM } from '@/utils/trackHoverPopup'
import { TRACK_LINE_STYLE } from '@/utils/trackLineLayerFactory'
import type { TrackProfileAnalysis } from '@/utils/trackProfileAnalysis'
import { findNearestTrackProfilePoint } from '@/utils/trackProfileAnalysis'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'TrackEditReferenceLayer' })

const props = defineProps<{
  segments: TrackEditReferenceDiffSegment[]
  beforeLayerId?: string | null
  // Profile analysis of the original (pre-edit) track; enables elevation and
  // metric details in the hover popup when provided.
  analysis?: TrackProfileAnalysis | null
  hoverTitle?: string
}>()

const mapInstance = inject<ShallowRef<maplibregl.Map | null>>('mapInstance')
const SOURCE_ID = 'track-edit-reference-source'
const HALO_LAYER_ID = 'track-edit-reference-halo-layer'
const LAYER_ID = 'track-edit-reference-layer'
const TOUCH_LAYER_ID = 'track-edit-reference-touch-layer'
const FOREGROUND_LAYER_IDS = new Set(['photo-clusters', 'photo-unclustered-point'])
const FOREGROUND_LAYER_PREFIXES = ['clusters-', 'unclustered-point-', 'cluster-count-']
let boundMap: maplibregl.Map | null = null
let lastRenderedSegments: TrackEditReferenceDiffSegment[] | null = null
let lastRenderedBeforeLayerId: string | undefined = undefined
const hoverPopup = shallowRef<maplibregl.Popup | null>(null)

const hideHoverPopup = () => {
  if (hoverPopup.value) {
    hoverPopup.value.remove()
    hoverPopup.value = null
  }
}

const coerceFeatureNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const showHoverPopup = (event: maplibregl.MapLayerMouseEvent) => {
  const map = mapInstance?.value
  if (!map) return

  const feature = event.features?.[0]
  if (!feature || feature.geometry.type !== 'LineString') return

  const segmentCoordinates = feature.geometry.coordinates.filter(
    (coordinate): coordinate is [number, number] =>
      Array.isArray(coordinate) &&
      coordinate.length >= 2 &&
      typeof coordinate[0] === 'number' &&
      typeof coordinate[1] === 'number',
  )
  const startDistanceM = coerceFeatureNumber(feature.properties?.startDistanceM) ?? 0
  const hoveredSegmentDistanceM = estimateHoveredDistanceM(segmentCoordinates, event.lngLat)
  const currentDistanceM =
    hoveredSegmentDistanceM === null ? startDistanceM : startDistanceM + hoveredSegmentDistanceM

  const analysis = props.analysis ?? null
  const nearestPoint = analysis
    ? findNearestTrackProfilePoint(analysis.points, currentDistanceM)
    : null
  const hoveredLeg = analysis?.legs.length
    ? (analysis.legs.find(
        (leg) => currentDistanceM >= leg.startDistanceM && currentDistanceM <= leg.endDistanceM,
      ) ?? analysis.legs[analysis.legs.length - 1])
    : null

  if (!hoverPopup.value) {
    hoverPopup.value = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'tour-popup',
      offset: 15,
    })
  }

  hoverPopup.value
    .setLngLat(event.lngLat)
    .setHTML(
      buildTrackHoverPopupHtml({
        title: props.hoverTitle?.trim() || 'Original route',
        currentDistanceM,
        elevationM: nearestPoint?.ele ?? null,
        segmentDistanceM: hoveredLeg?.distanceM ?? null,
        metricId: analysis?.metricId,
        categoryLabel: hoveredLeg?.categoryLabel ?? null,
        rawValue: hoveredLeg?.rawValue ?? null,
      }),
    )
    .addTo(map)

  try {
    const element = hoverPopup.value.getElement?.() as HTMLElement | undefined
    if (element) {
      element.style.zIndex = Z_INDEX.POPOVER.toString()
    }
  } catch {
    // Ignore popup element access errors from maplibre typings/runtime variance.
  }
}

const handleHoverMouseEnter = (event: maplibregl.MapLayerMouseEvent) => {
  const map = mapInstance?.value
  if (!map) return
  map.getCanvas().style.cursor = 'pointer'
  showHoverPopup(event)
}

const handleHoverMouseMove = (event: maplibregl.MapLayerMouseEvent) => {
  showHoverPopup(event)
}

const handleHoverMouseLeave = () => {
  const map = mapInstance?.value
  if (map) {
    map.getCanvas().style.cursor = ''
  }
  hideHoverPopup()
}

const unbindMapListeners = (map: maplibregl.Map | null | undefined) => {
  if (!map) return
  map.off('load', updateReferenceLayer)
  map.off('styledata', updateReferenceLayer)
  map.off('mouseenter', TOUCH_LAYER_ID, handleHoverMouseEnter)
  map.off('mousemove', TOUCH_LAYER_ID, handleHoverMouseMove)
  map.off('mouseleave', TOUCH_LAYER_ID, handleHoverMouseLeave)
}

const removeReferenceLayer = (map: maplibregl.Map | null | undefined) => {
  hideHoverPopup()
  if (!map) return
  if (map.getLayer(TOUCH_LAYER_ID)) map.removeLayer(TOUCH_LAYER_ID)
  if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID)
  if (map.getLayer(HALO_LAYER_ID)) map.removeLayer(HALO_LAYER_ID)
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
  lastRenderedSegments = null
  lastRenderedBeforeLayerId = undefined
}

const resolveBeforeLayerId = (map: maplibregl.Map) => {
  const layers = map.getStyle().layers ?? []
  for (const layer of layers) {
    const layerId = layer.id
    if (
      FOREGROUND_LAYER_IDS.has(layerId) ||
      FOREGROUND_LAYER_PREFIXES.some((prefix) => layerId.startsWith(prefix))
    ) {
      return layerId
    }
  }

  if (
    props.beforeLayerId &&
    map.getLayer(props.beforeLayerId) &&
    !props.beforeLayerId.includes('-outline-')
  ) {
    return props.beforeLayerId
  }

  return undefined
}
const updateReferenceLayer = () => {
  if (!mapInstance?.value) {
    removeReferenceLayer(mapInstance?.value)
    return
  }

  const map = mapInstance.value
  if (props.segments.length === 0) {
    removeReferenceLayer(map)
    return
  }
  const beforeLayerId = resolveBeforeLayerId(map)
  const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined
  const hasLayers =
    !!map.getLayer(HALO_LAYER_ID) && !!map.getLayer(LAYER_ID) && !!map.getLayer(TOUCH_LAYER_ID)
  if (
    source &&
    hasLayers &&
    lastRenderedSegments === props.segments &&
    lastRenderedBeforeLayerId === beforeLayerId
  ) {
    return
  }

  const data: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: props.segments.map((segment) => ({
      type: 'Feature',
      properties: {
        startDistanceM: segment.startDistanceM,
      },
      geometry: {
        type: 'LineString',
        coordinates: segment.coordinates,
      },
    })),
  }

  if (source) {
    source.setData(data)
  } else {
    map.addSource(SOURCE_ID, { type: 'geojson', data })
    map.addLayer(
      {
        id: HALO_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#ffffff',
          'line-width': 7,
          'line-opacity': 0.95,
        },
      },
      beforeLayerId,
    )
    map.addLayer(
      {
        id: LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#f87171',
          'line-width': 4,
          'line-opacity': 0.8,
          'line-dasharray': [1.25, 1.25],
        },
      },
      beforeLayerId,
    )
    map.addLayer(
      {
        id: TOUCH_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': 'transparent',
          'line-width': TRACK_LINE_STYLE.touchTargetWidth,
          'line-opacity': 0,
        },
      },
      beforeLayerId,
    )
  }

  if (
    beforeLayerId &&
    map.getLayer(LAYER_ID) &&
    map.getLayer(HALO_LAYER_ID) &&
    map.getLayer(TOUCH_LAYER_ID) &&
    map.getLayer(beforeLayerId) &&
    beforeLayerId !== HALO_LAYER_ID &&
    beforeLayerId !== LAYER_ID &&
    beforeLayerId !== TOUCH_LAYER_ID
  ) {
    map.moveLayer(HALO_LAYER_ID, beforeLayerId)
    map.moveLayer(LAYER_ID, beforeLayerId)
    map.moveLayer(TOUCH_LAYER_ID, beforeLayerId)
  }

  lastRenderedSegments = props.segments
  lastRenderedBeforeLayerId = beforeLayerId
}

watch(() => props.segments, updateReferenceLayer)
watch(() => props.beforeLayerId, updateReferenceLayer)

watch(
  () => mapInstance?.value,
  (map, previousMap) => {
    if (previousMap && previousMap !== map) {
      unbindMapListeners(previousMap)
      removeReferenceLayer(previousMap)
    }
    if (!map) return
    if (boundMap !== map) {
      unbindMapListeners(boundMap)
      map.on('load', updateReferenceLayer)
      map.on('styledata', updateReferenceLayer)
      map.on('mouseenter', TOUCH_LAYER_ID, handleHoverMouseEnter)
      map.on('mousemove', TOUCH_LAYER_ID, handleHoverMouseMove)
      map.on('mouseleave', TOUCH_LAYER_ID, handleHoverMouseLeave)
      boundMap = map
    }
    if (map.loaded() || map.isStyleLoaded()) {
      updateReferenceLayer()
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  unbindMapListeners(boundMap)
  boundMap = null
  removeReferenceLayer(mapInstance?.value)
})
</script>

<template>
  <slot></slot>
</template>
